import type { ResourceField } from '@/lib/resources'

const SYSTEM_NAMES = new Set([
  'id',
  'pk',
  'created_at',
  'updated_at',
  'modified_at',
  'deleted_at',
  'created_by',
  'modified_by',
  'updated_by',
  'deleted_by',
  'created',
  'modified',
  'updated',
  'deleted',
  'password',
  'secret',
  'token',
  'api_key',
  'api_secret',
  'hash',
  'salt',
  'nonce',
  'signature',
  'last_login',
  'date_joined',
  'is_superuser',
  'is_staff'
])

const CORE_NAMES = new Set([
  'name',
  'title',
  'subject',
  'username',
  'email',
  'label',
  'slug',
  'description',
  'summary',
  'bio',
  'content',
  'body',
  'comment',
  'message',
  'notes',
  'status',
  'type',
  'kind',
  'category',
  'role',
  'state',
  'stage',
  'phase',
  'phone',
  'mobile',
  'telephone',
  'address',
  'city',
  'state',
  'country',
  'zip',
  'postal_code',
  'url',
  'website',
  'link',
  'image',
  'avatar',
  'photo',
  'icon',
  'logo',
  'price',
  'amount',
  'cost',
  'fee',
  'rate',
  'total',
  'subtotal',
  'tax',
  'quantity',
  'count',
  'size',
  'weight',
  'first_name',
  'last_name',
  'full_name',
  'display_name',
  'nickname',
  'language',
  'timezone',
  'currency',
  'locale',
  'start_date',
  'end_date',
  'start_time',
  'end_time',
  'due_date',
  'published_at',
  'sort_order',
  'position',
  'priority',
  'rank',
  'order',
  'color',
  'theme',
  'layout',
  'format',
  'owner',
  'author',
  'parent',
  'featured',
  'customer',
  'member',
  'partner',
  'sponsor',
  'handle',
  'tag',
  'topic',
  'genre',
  'rating',
  'score',
  'votes',
  'views',
  'likes',
  'shares',
  'difficulty',
  'level',
  'grade',
  'discount',
  'coupon',
  'promo'
])

const TIMESTAMP_SUFFIXES = ['_at', '_date', '_since', '_until']
const FLAG_PREFIXES = ['is_', 'has_', 'can_', 'should_', 'was_']

export interface FieldGroup {
  label: string
  fields: ResourceField[]
  collapsible?: boolean
  defaultCollapsed?: boolean
}

export function isSystemField(field: ResourceField): boolean {
  return SYSTEM_NAMES.has(field.name)
}

export function isCoreField(field: ResourceField): boolean {
  if (isSystemField(field)) return false
  if (field.required) return true
  if (CORE_NAMES.has(field.name)) return true
  return false
}

export function isTimestampField(field: ResourceField): boolean {
  return (
    TIMESTAMP_SUFFIXES.some((s) => field.name.endsWith(s)) &&
    (field.type.toLowerCase().includes('date') ||
      field.type.toLowerCase().includes('time'))
  )
}

export function isFlagField(field: ResourceField): boolean {
  return (
    FLAG_PREFIXES.some((p) => field.name.startsWith(p)) &&
    field.type.toLowerCase().includes('bool')
  )
}

export function categorizeFields(fields: ResourceField[]): FieldGroup[] {
  const system: ResourceField[] = []
  const timestamps: ResourceField[] = []
  const flags: ResourceField[] = []
  const relations: ResourceField[] = []
  const content: ResourceField[] = []

  for (const field of fields) {
    if (isSystemField(field)) {
      system.push(field)
    } else if (isTimestampField(field)) {
      timestamps.push(field)
    } else if (isFlagField(field)) {
      flags.push(field)
    } else if (
      field.name.endsWith('_id') ||
      field.widget === 'fk' ||
      field.widget === 'fk-multi'
    ) {
      relations.push(field)
    } else {
      content.push(field)
    }
  }

  const groups: FieldGroup[] = []
  if (content.length > 0) {
    groups.push({ label: 'Details', fields: content })
  }
  if (relations.length > 0) {
    groups.push({
      label: 'Relations',
      fields: relations,
      collapsible: true,
      defaultCollapsed: true
    })
  }
  if (flags.length > 0) {
    groups.push({
      label: 'Flags & Settings',
      fields: flags,
      collapsible: true,
      defaultCollapsed: true
    })
  }
  if (timestamps.length > 0) {
    groups.push({
      label: 'Timestamps',
      fields: timestamps,
      collapsible: true,
      defaultCollapsed: true
    })
  }
  if (system.length > 0) {
    groups.push({
      label: 'System',
      fields: system,
      collapsible: true,
      defaultCollapsed: true
    })
  }
  return groups
}

export interface EssentialGroup {
  core: ResourceField[]
  extra: ResourceField[]
}

export function splitEssential(fields: ResourceField[]): EssentialGroup {
  const core: ResourceField[] = []
  const extra: ResourceField[] = []
  for (const f of fields) {
    if (isCoreField(f)) {
      core.push(f)
    } else {
      extra.push(f)
    }
  }
  return { core, extra }
}

export function getVisibleListFields(
  listFieldNames: string[],
  allFields: ResourceField[],
  maxColumns = 5
): string[] {
  const fieldMap = new Map(allFields.map((f) => [f.name, f]))
  const visible = listFieldNames.filter((name) => {
    const field = fieldMap.get(name)
    if (!field) return true
    if (isSystemField(field)) return false
    return true
  })

  if (visible.length <= maxColumns) return visible

  const { core } = splitEssential(
    visible.map((n) => fieldMap.get(n)).filter(Boolean) as ResourceField[]
  )

  const coreNames = new Set(core.map((f) => f.name))
  const result = visible.filter((n) => coreNames.has(n))
  if (result.length === 0) return visible.slice(0, maxColumns)
  return result.slice(0, maxColumns)
}
